import React, { useRef } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { TextEditor } from "src/base-components/TextEditor/TextEditor";
import { Toolbar } from "src/base-components/TextEditor/Toolbar";
import { FeatureType } from "src/clients/features-control";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { QueryFormValues } from "src/featureCatalogue/Queries/Create/schema";

export const FeatureFieldInput: React.FC<{
  fieldName: `columns.features.${number}.${string}`;
  label: string;
  dataLoc: string;
  placeholder: string;
  disabled?: boolean;
  prefixIcon?: { icon: IconProp };
  tooltip?: string;
  trimOnChange?: boolean;
}> = ({
  fieldName,
  label,
  dataLoc,
  placeholder,
  disabled,
  prefixIcon,
  tooltip,
  trimOnChange,
}) => {
  return (
    <div>
      <Label>
        <div className="flex items-center gap-x-1">
          {label}
          {tooltip && (
            <Tooltip body={tooltip}>
              <Icon icon={faInfoCircle} size="2xs" />
            </Tooltip>
          )}
        </div>
      </Label>
      <Controller
        name={fieldName}
        render={({ field, fieldState }) => (
          <>
            <Input
              data-loc={dataLoc}
              disabled={disabled}
              errored={!!fieldState.error?.message}
              placeholder={placeholder}
              prefixIcon={prefixIcon}
              value={field.value ?? ""}
              fullWidth
              onChange={
                trimOnChange
                  ? (e) => field.onChange(e.target.value.trim())
                  : field.onChange
              }
            />
            {fieldState.error?.message && (
              <ErrorHint dataLoc={`${dataLoc}-error`}>
                {fieldState.error.message}
              </ErrorHint>
            )}
          </>
        )}
      />
    </div>
  );
};

// `column_name` is determined by the SQL preview, not by the user — each
// feature is 1:1 with a column in the result set. Rendering this as a
// read-only badge prevents footguns: picking the same column for two
// features, silently re-pointing a feature to a renamed alias (and breaking
// downstream consumers of the `feature_key`), or selecting a column that
// later disappears from the preview.
export const ColumnNameDisplay: React.FC<{
  index: number;
  columnsOptions: Array<{
    key: string;
    value: string;
    icon: IconProp;
    type: FeatureType | undefined;
  }>;
}> = ({ index, columnsOptions }) => {
  const form = useFormContext<QueryFormValues>();
  const columnName = useWatch({
    control: form.control,
    name: `columns.features.${index}.column_name`,
  });

  const option = columnsOptions.find((opt) => opt.key === columnName);
  const displayValue = option?.value ?? columnName ?? "";

  return (
    <div>
      <Label>Column Name</Label>
      <div
        className="flex h-9 items-center gap-x-2 rounded-lg border border-gray-200 bg-gray-50 px-3 font-inter-normal-12px text-gray-700"
        data-loc={`feature-column-${index}`}
      >
        {option?.icon && <Icon icon={option.icon} size="2xs" />}
        <span className="truncate">{displayValue}</span>
      </div>
    </div>
  );
};

export const FeatureDescriptionEditor: React.FC<{
  disabled?: boolean;
  index: number;
}> = ({ disabled, index }) => {
  const toolbarRef = useRef<HTMLUListElement>(null);
  return (
    <>
      <Label>Feature description</Label>
      <div className="flex flex-col rounded border border-gray-200 bg-white p-3">
        <Controller
          name={`columns.features.${index}.description`}
          render={({ field }) => (
            <TextEditor
              dataLoc={`feature-description-${index}`}
              placeholder="Placeholder..."
              readOnly={disabled}
              toolbarRef={toolbarRef}
              value={field.value ?? ""}
              onChange={field.onChange}
            />
          )}
        />
        <div className="flex justify-between gap-x-3 pt-3">
          <Toolbar ref={toolbarRef} exclude={["heading"]} />
        </div>
      </div>
    </>
  );
};
